import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  Users,
  Smartphone,
  BarChart3,
  Target,
  TrendingUp,
  UtensilsCrossed,
  Ticket,
  Car,
  Scale,
  Building2,
  Plane,
  GraduationCap,
} from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import StatCounter from "../components/site/StatCounter";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";

const HERO_IMG =
  "https://images.unsplash.com/photo-1480843669328-3f7e37d196ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400";
const STADIUM_IMG =
  "https://images.unsplash.com/photo-1577223625816-7546f13df25d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function HomePage() {
  useReveal();
  useSEO({
    title: "Rae Enterprise LLC | Luxury Geofencing & Programmatic Display Ads",
    description: "Seattle's choice in top quality marketing. We use real-world movement and behavioral intelligence to put your brand in front of the right people, at the right place, at the right time.",
    path: "/",
  });
  return (
    <SiteShell>
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden min-h-[92vh] flex items-center" data-testid="hero-section">
        {/* background */}
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 kenburns"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.55) saturate(0.7)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0b] via-transparent to-transparent" />
          {/* gold pins */}
          <Pin top="22%" left="62%" />
          <Pin top="48%" left="78%" delay="1.4s" />
          <Pin top="68%" left="55%" delay="2.6s" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 w-full pt-24 pb-20">
          <div className="max-w-[720px]">
            <div className="kicker mb-8 flex items-center gap-3">
              <span className="gold-rule" /> Luxury Geofencing &amp; Programmatic Display Ads
            </div>
            <h1
              className="h-display text-white text-[64px] sm:text-[88px] lg:text-[120px] tracking-tight"
              data-testid="hero-headline"
            >
              OWN
              <br />
              ATTENTION<span className="text-[#d4af37]">.</span>
            </h1>
            <div className="kicker mt-6 text-white/50" data-testid="hero-subkicker">
              Military-Grade Ad Tech · Geofencing · Programmatic Display
            </div>
            <p className="mt-10 max-w-[520px] text-[15px] md:text-[17px] text-white/75 leading-relaxed">
              We use real-world movement and behavioral intelligence to put your brand in front
              of the right people, at the right place, at the right time.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <Link to="/services" className="btn-gold btn-label" data-testid="hero-cta-primary">
                See How It Works <ArrowRight size={16} strokeWidth={1.6} />
              </Link>
              <Link to="/campaign-demo" className="btn-outline-gold btn-label" data-testid="hero-cta-secondary">
                View Campaign Demo
              </Link>
            </div>

            <p className="mt-14 kicker text-white/50">
              Seattle's choice in top quality marketing.
            </p>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="py-28 md:py-36 bg-[#0b0b0b]" data-testid="how-it-works-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="text-center mb-20 reveal">
            <div className="kicker mb-5">How Geofencing Works</div>
            <h2 className="h-display text-white text-[40px] sm:text-[56px] lg:text-[72px]">
              PRECISION. <span className="text-white/70">INTENT.</span> <span className="text-[#d4af37]">RESULTS.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-6">
            <Step
              n="01"
              title="Target Real-World Locations"
              desc="We build custom geofences around the physical places your audience visits — venues, competitors, neighborhoods, events."
              icon={<MapPin size={28} strokeWidth={1.2} />}
              testId="step-1"
            />
            <Step
              n="02"
              title="Capture High-Intent Audiences"
              desc="We identify and capture mobile devices entering your targeted locations, building a high-intent audience profile."
              icon={<Users size={28} strokeWidth={1.2} />}
              testId="step-2"
            />
            <Step
              n="03"
              title="Retarget Across 600K+ Sites & Apps"
              desc="We retarget your captured audience across premium digital platforms with your message until they convert."
              icon={<Smartphone size={28} strokeWidth={1.2} />}
              testId="step-3"
            />
          </div>
        </div>
      </section>

      {/* ============== STATS BAR ============== */}
      <section className="bg-[#111111] border-y border-[#d4af37]/15" data-testid="stats-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#d4af37]/15">
            <Stat
              icon={<BarChart3 size={20} strokeWidth={1.2} className="text-[#d4af37]" />}
              value="3.2M+"
              label="Impressions Delivered"
              sub="Precision. Scale. Performance."
              testId="stat-impressions"
            />
            <Stat
              icon={<MapPin size={20} strokeWidth={1.2} className="text-[#d4af37]" />}
              value="1,250+"
              label="Geofences Deployed"
              sub="From venues to competitors to events."
              testId="stat-geofences"
            />
            <Stat
              icon={<Target size={20} strokeWidth={1.2} className="text-[#d4af37]" />}
              value="21+"
              label="Industries Served"
              sub="Hospitality, Events, Automotive, Real Estate & More."
              testId="stat-industries"
            />
            <Stat
              icon={<TrendingUp size={20} strokeWidth={1.2} className="text-[#d4af37]" />}
              value="12%"
              label="Average Lift in Foot Traffic"
              sub="Measured through conversion zones."
              testId="stat-foot-traffic"
            />
          </div>
        </div>
      </section>

      {/* ============== STADIUM DEMO ============== */}
      <section className="relative py-28 md:py-36 overflow-hidden" data-testid="stadium-section">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${STADIUM_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "brightness(0.45)",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b] via-[#0b0b0b]/85 to-[#0b0b0b]/30" />
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="kicker mb-5">See the Power of Location</div>
            <h3 className="h-display text-white text-[36px] sm:text-[48px] lg:text-[58px] leading-[1.0]">
              IMAGINE TARGETING EVERYONE INSIDE THIS STADIUM<span className="text-[#d4af37]">.</span>
            </h3>
            <span className="gold-rule my-8 block" />
            <p className="text-white/70 max-w-[440px]">
              Then following them across the internet with your brand — for days, weeks, even
              months after they leave the venue.
            </p>
            <Link to="/campaign-demo" className="btn-gold btn-label mt-10 inline-flex" data-testid="stadium-cta">
              View Interactive Demo <ArrowRight size={16} strokeWidth={1.6} />
            </Link>
          </div>

          {/* floating metric card */}
          <div
            className="reveal relative bg-[#111111]/85 backdrop-blur-md border border-[#d4af37]/25 p-8 lg:p-10 max-w-[440px] ml-auto"
            data-testid="stadium-metric-card"
          >
            <div className="kicker mb-6 flex items-center gap-3">
              <Target size={14} strokeWidth={1.4} className="text-[#d4af37]" /> Target: Lumen Field
            </div>
            <ul className="space-y-5">
              <Metric label="People Entered" value="66,925" />
              <Metric label="Date" value="June 19" />
              <Metric label="Impressions" value="468,475" />
              <Metric label="Foot Traffic Lift" value="+12%" highlight />
            </ul>
            <Link
              to="/campaign-demo"
              className="mt-8 inline-flex items-center gap-2 text-[#d4af37] btn-label"
              data-testid="stadium-report-link"
            >
              View Full Report <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== INDUSTRIES ============== */}
      <section className="py-24 bg-[#0b0b0b] border-t border-white/5" data-testid="industries-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <div className="kicker">Industries We Serve</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-10">
            <Industry icon={<UtensilsCrossed size={26} strokeWidth={1.1} />} label="Hospitality" />
            <Industry icon={<Ticket size={26} strokeWidth={1.1} />} label="Events & Venues" />
            <Industry icon={<Car size={26} strokeWidth={1.1} />} label="Automotive" />
            <Industry icon={<Scale size={26} strokeWidth={1.1} />} label="Legal" />
            <Industry icon={<Building2 size={26} strokeWidth={1.1} />} label="Real Estate" />
            <Industry icon={<Plane size={26} strokeWidth={1.1} />} label="Tourism" />
            <Industry icon={<GraduationCap size={26} strokeWidth={1.1} />} label="Higher Education" />
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function Pin({ top, left, delay = "0s" }) {
  return (
    <div className="absolute" style={{ top, left }}>
      <div className="relative w-3 h-3">
        <div className="absolute inset-0 rounded-full bg-[#d4af37]" />
        <div className="pulse-ring inset-0 w-3 h-3" style={{ animationDelay: delay }} />
        <div className="pulse-ring inset-0 w-3 h-3" style={{ animationDelay: `calc(${delay} + 1s)` }} />
      </div>
    </div>
  );
}

function Step({ n, title, desc, icon, testId }) {
  return (
    <div className="reveal" data-testid={testId}>
      <div className="flex items-center gap-5 mb-6">
        <div className="text-[#d4af37] font-display text-[36px] leading-none">{n}</div>
        <div className="text-[#d4af37]">{icon}</div>
      </div>
      <h4 className="text-white text-[18px] tracking-[0.15em] uppercase mb-4 font-medium">
        {title}
      </h4>
      <p className="text-white/65 text-[15px] leading-relaxed max-w-[300px]">{desc}</p>
    </div>
  );
}

function Stat({ icon, value, label, sub, testId }) {
  return (
    <div className="px-4 sm:px-6 md:px-8 py-6 first:pl-0 min-w-0">
      <div className="mb-3">{icon}</div>
      <div className="font-display text-[#d4af37] text-[clamp(28px,3.3vw,56px)] leading-none mb-3 whitespace-nowrap">
        <StatCounter value={value} testId={testId} />
      </div>
      <div className="text-white text-[11px] md:text-[12px] tracking-[0.22em] uppercase mb-2">{label}</div>
      <div className="text-white/45 text-[12px] tracking-wide">{sub}</div>
    </div>
  );
}

function Metric({ label, value, highlight }) {
  return (
    <li className="flex items-center justify-between border-b border-white/10 pb-3">
      <span className="text-[12px] tracking-[0.18em] uppercase text-white/55">{label}</span>
      <span className={`font-display text-[20px] ${highlight ? "text-[#d4af37]" : "text-white"}`}>
        {value}
      </span>
    </li>
  );
}

function Industry({ icon, label }) {
  return (
    <div
      className="flex flex-col items-center gap-3 text-[#d4af37] group cursor-default"
      data-testid={`industry-${label.toLowerCase().replace(/\s+|&/g, "-")}`}
    >
      <div className="transition-transform group-hover:-translate-y-1">{icon}</div>
      <div className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-white/75 text-center">
        {label}
      </div>
    </div>
  );
}
